import { AxiosError, AxiosInstance } from "axios";
import axiosRetry from "axios-retry";
import { appInsights } from "utils/appInsights";

function urlPatternstsToInclude() {
	return [
		"Authentication/passwordreset",
		"passwordrules",
	];
}

function SetupAxiosInterceptors(axios: AxiosInstance) {

	function getRequestStartTime(startTime: any): number {
		if (startTime) {
			return new Date(startTime)?.getTime() || 0;
		}
		return 0;
	}

	axios.interceptors.request.use((config) => {
		config.headers.startTime = new Date();

		return config;
	});

	axios.interceptors.response.use(
		(response) => {
			try {
				let requestStartTime = getRequestStartTime(response.config.headers?.startTime);

				const duration = new Date().getTime() - requestStartTime;
				const requestBody = response.config.data ? JSON.stringify(response.config.data) : null;
				const responseBody = response.data ? JSON.stringify(response.data) : null;

				if (response.config.url && urlPatternstsToInclude().some(url => response.config.url?.includes(url))) {
					appInsights.trackDependencyData({
						target: response.config.url,
						name: `Axios ${response.config.method?.toUpperCase()}`,
						duration: duration,
						success: response.status >= 200 && response.status < 300,
						responseCode: response.status,
						id: window.crypto.randomUUID(),
						properties: {
							url: response.config.url,
							userAgent : navigator.userAgent,
							requestBody: requestBody,
							responseBody: responseBody,
							retryCount: response.config["axios-retry"]?.retryCount
						},
					});
				}
			} catch (error) {
				console.log("Error logging telemetry data:", error);
			}

			return response;
		},
		(error) => {
			try {
				let requestStartTime = getRequestStartTime(error.config.headers?.startTime);

				const duration = new Date().getTime() - requestStartTime;
				const requestBody = error.config.data ? JSON.stringify(error.config.data) : null;
				const errorResponseBody = error.response?.data ? JSON.stringify(error.response?.data) : null;

				if (error.config.url && urlPatternstsToInclude().some(url => error.config.url.includes(url))) {
					appInsights.trackDependencyData({
						target: error.config.url,
						name: `Axios ${error.config.method?.toUpperCase()}`,
						duration: duration,
						success: false,
						responseCode: error.response?.status,
						id: window.crypto.randomUUID(),
						properties: {
							url: error.config.url,
							userAgent : navigator.userAgent,
							requestBody: requestBody,
							responseBody: errorResponseBody,
							retryCount: error.config["axios-retry"]?.retryCount
						},
					});
				}

			} catch (loggingError) {
				console.log("Error logging telemetry data:", loggingError);
			}

			return Promise.reject(error);
		}
	);
}

function SetupAxiosRetryInterceptors(axios: AxiosInstance) {

	try {
		axiosRetry(axios, {
			retries: 3,
			retryDelay: (retryCount: number) => {
				return retryCount * 2000;
			},
			retryCondition: (error: AxiosError) => {
			
				let condition1 = (error.response?.status ?? 0) > 200;
				let condition2 = urlPatternstsToInclude().some(pattern => error.config?.url?.includes(pattern));

				return condition1 && condition2;
			},
		});
	} catch (error) {
		console.error("Error setting up Axios retry interceptors:", error);
	}
}

export function SetupAxiosInterceptorsAndRetry(axios: AxiosInstance) {
	SetupAxiosInterceptors(axios);
	SetupAxiosRetryInterceptors(axios);
}